/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper";
import { SqlVulnerabilityAssessmentScans } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { SqlManagementClient } from "../sqlManagementClient";
import {
  SqlVulnerabilityAssessmentScanRecord,
  VulnerabilityAssessmentName,
  SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsNextOptionalParams,
  SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsOptionalParams,
  SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsResponse,
  SqlVulnerabilityAssessmentScansGetOptionalParams,
  SqlVulnerabilityAssessmentScansGetResponse,
  SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsNextResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing SqlVulnerabilityAssessmentScans operations. */
export class SqlVulnerabilityAssessmentScansImpl
  implements SqlVulnerabilityAssessmentScans {
  private readonly client: SqlManagementClient;

  /**
   * Initialize a new instance of the class SqlVulnerabilityAssessmentScans class.
   * @param client Reference to the service client
   */
  constructor(client: SqlManagementClient) {
    this.client = client;
  }

  /**
   * Lists the vulnerability assessment scans of a database.
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param options The options parameters.
   */
  public listBySqlVulnerabilityAssessments(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    options?: SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsOptionalParams
  ): PagedAsyncIterableIterator<SqlVulnerabilityAssessmentScanRecord> {
    const iter = this.listBySqlVulnerabilityAssessmentsPagingAll(
      resourceGroupName,
      serverName,
      vulnerabilityAssessmentName,
      options
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listBySqlVulnerabilityAssessmentsPagingPage(
          resourceGroupName,
          serverName,
          vulnerabilityAssessmentName,
          options,
          settings
        );
      }
    };
  }

  private async *listBySqlVulnerabilityAssessmentsPagingPage(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    options?: SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsOptionalParams,
    settings?: PageSettings
  ): AsyncIterableIterator<SqlVulnerabilityAssessmentScanRecord[]> {
    let result: SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listBySqlVulnerabilityAssessments(
        resourceGroupName,
        serverName,
        vulnerabilityAssessmentName,
        options
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listBySqlVulnerabilityAssessmentsNext(
        resourceGroupName,
        serverName,
        vulnerabilityAssessmentName,
        continuationToken,
        options
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listBySqlVulnerabilityAssessmentsPagingAll(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    options?: SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsOptionalParams
  ): AsyncIterableIterator<SqlVulnerabilityAssessmentScanRecord> {
    for await (const page of this.listBySqlVulnerabilityAssessmentsPagingPage(
      resourceGroupName,
      serverName,
      vulnerabilityAssessmentName,
      options
    )) {
      yield* page;
    }
  }

  /**
   * Lists the vulnerability assessment scans of a database.
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param options The options parameters.
   */
  private _listBySqlVulnerabilityAssessments(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    options?: SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsOptionalParams
  ): Promise<
    SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsResponse
  > {
    return this.client.sendOperationRequest(
      { resourceGroupName, serverName, vulnerabilityAssessmentName, options },
      listBySqlVulnerabilityAssessmentsOperationSpec
    );
  }

  /**
   * Get a system database vulnerability assessment scan record.
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param scanId
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    scanId: string,
    options?: SqlVulnerabilityAssessmentScansGetOptionalParams
  ): Promise<SqlVulnerabilityAssessmentScansGetResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        serverName,
        vulnerabilityAssessmentName,
        scanId,
        options
      },
      getOperationSpec
    );
  }

  /**
   * ListBySqlVulnerabilityAssessmentsNext
   * @param resourceGroupName The name of the resource group that contains the resource. You can obtain
   *                          this value from the Azure Resource Manager API or the portal.
   * @param serverName The name of the server.
   * @param vulnerabilityAssessmentName The name of the vulnerability assessment.
   * @param nextLink The nextLink from the previous successful call to the
   *                 ListBySqlVulnerabilityAssessments method.
   * @param options The options parameters.
   */
  private _listBySqlVulnerabilityAssessmentsNext(
    resourceGroupName: string,
    serverName: string,
    vulnerabilityAssessmentName: VulnerabilityAssessmentName,
    nextLink: string,
    options?: SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsNextOptionalParams
  ): Promise<
    SqlVulnerabilityAssessmentScansListBySqlVulnerabilityAssessmentsNextResponse
  > {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        serverName,
        vulnerabilityAssessmentName,
        nextLink,
        options
      },
      listBySqlVulnerabilityAssessmentsNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listBySqlVulnerabilityAssessmentsOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Sql/servers/{serverName}/sqlVulnerabilityAssessments/{vulnerabilityAssessmentName}/scans",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SqlVulnerabilityAssessmentScanRecordListResult
    },
    default: {}
  },
  queryParameters: [Parameters.apiVersion4, Parameters.systemDatabaseName],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.serverName,
    Parameters.vulnerabilityAssessmentName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const getOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Sql/servers/{serverName}/sqlVulnerabilityAssessments/{vulnerabilityAssessmentName}/scans/{scanId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SqlVulnerabilityAssessmentScanRecord
    },
    default: {}
  },
  queryParameters: [Parameters.apiVersion4, Parameters.systemDatabaseName],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.serverName,
    Parameters.vulnerabilityAssessmentName,
    Parameters.scanId
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listBySqlVulnerabilityAssessmentsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SqlVulnerabilityAssessmentScanRecordListResult
    },
    default: {}
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.serverName,
    Parameters.nextLink,
    Parameters.vulnerabilityAssessmentName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
